import React, { useContext, useEffect, useMemo, useState } from "react";
import { useWindowSize } from "@reach/window-size";
import ToggleButton from "../../../../components/common/ToggleButton/ToggleButton";
import CustomTable from "../../../../components/common/CustomTable";
import { useParams } from "react-router-dom";
import {
  StyledTransactionsTable,
  ToggleContainer,
} from "../TransactionsTable/style";
import {
  capitalizeName,
  CURRENCIES_TO_ALLOW_FOR_SPECIFIC_STABLE_COINS,
  extractedString,
  formatBankName,
  formatValue,
  getErrorMessage,
  payIntableColumns,
  tableColumns,
} from "../../utils";
import { useAppSelector } from "../../../../app/hooks";
import dayjs from "dayjs";
import Loader from "../../../../components/common/Loader";
import Tools from "../Tools";
import { getPayoutTransactions, getPayInTransactions } from "../../api";
import { formatCurrencyAmount } from "../../../../utils/currency";
import {
  EmptyWrapper,
  EmptyWrapperTitle,
  EmptyWrapperSubTitle,
} from "../../../Balances/style";
import { MobileBody } from "../../../Transactions/style";
import EmptyMobileCard from "../../../../components/emptyStates/EmptyMobileCard";
import WalletCard from "../WalletCard";

import Drawer from "../Drawer/Drawer";
import CustomMobileTable from "../../../../components/common/CustomMobileTable";
import CustomModal from "../../../../components/common/CustomModal";
import ExportByFilterModal from "../../../../components/common/ExportByFilterModal";
import { canUserExport, capitalizeCurrencyCode, convertToDateOnly } from "../../../../utils/helpers";
import { selectUserRoleAndPermissions } from "../../../Settings/Team/redux/slice";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import { SuccessAlertContext } from "../../../../context/SuccessAlertContext";
import { stableCoinsArray, stableCoinsList } from "../../const";
import { useWalletStatementExport } from "../../../../app/queries/wallets";
import { fileFormatApiMap } from "../../../../components/common/ExportByFilterModal/constants";

/* Opportunity to make this a reusable component: ANOTHER OPPORTUNITY TO REVAMP THIS PAGE */
const EmptyBalanceDescription = () => {
  return (
    <EmptyWrapper>
      <EmptyWrapperTitle>No transactions yet</EmptyWrapperTitle>
      <EmptyWrapperSubTitle>
        All received or sent transactions will appear here
      </EmptyWrapperSubTitle>
    </EmptyWrapper>
  );
};

interface DateProps {
  startDate: Date;
  endDate: Date;
}

const Table = () => {
  const { width } = useWindowSize();
  const { businessId } = useAppSelector((state) => state.users);
  const { permissions } = useAppSelector(selectUserRoleAndPermissions);
  const { mutate: initiateWalletStatementExport, isLoading: walletExportIsLoading } =
    useWalletStatementExport();

  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );

  const { onShowAlert: onShowSuccessAlert } = useContext(
    SuccessAlertContext,
  );

  const { wallets } = useAppSelector((state) => state.wallets);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    openDrawer: false,
    data: null,
  });
  /* Opportunity for improvement: Will be nice to consolidate the states into one */
  const [tableData, setTableData] = useState(null);
  const [debitData, setDebitData] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [totalPayInData, setTotalPayinData] = useState(null);
  const { currencyCode } = useParams();
  const [activeToggle, setActiveToggle] = useState("Credit");
  const toggleType = ["Debit", "Credit"];
  const [select, setSelect] = useState(null);
  const [status, setStatus] = useState<number>();
  const [date, setDate] = useState<DateProps>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [openExportFilterModal, setOpenExportFilterModal] =
    useState<boolean>(false);

  const currentWallet = wallets?.find(
    (wallet) => wallet?.currency === capitalizeCurrencyCode(currencyCode),
  );

  if (!currentWallet) {
    return <div>wallet not found</div>;
  }

  const startDate = date?.[0] && dayjs(date[0])?.format("YYYY-MM-DD");
  const endDate = date?.[1] && dayjs(date[1])?.format("YYYY-MM-DD");

  const onPageChange = (page: number, size: number) => {
    setPageNumber(page);
    setPageSize(size);
  };

  const filter = () => {
    getDebitTransaction();
  };

  const filterData = (data) => {
    return data?.filter((item) =>
      Object.values(item).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(search.toLowerCase()),
      ),
    );
  };

  const handleReset = async () => {
    setStatus(null);
    setSelect(null);
  };

  const handleToggle = (item: string) => {
    setActiveToggle(item);
  };

  const TABLE_TITLE = `${capitalizeCurrencyCode(currencyCode)} transactions`;

  const DEBIT_COLUMN = tableColumns;
  const getCreditTransaction = async () => {
    setLoading(true);
    try {
      const data = {
        toWallet: currentWallet?.id,
        pageNumber: pageNumber,
        pageSize: 10,
        ...(stableCoinsArray.includes(currencyCode?.toLowerCase()) && { businessId }),
      };

      await getPayInTransactions(data).then((data) => {
        setTotalPayinData(data);
        setTableData(data);

        const payInData = data?.walletTransactions?.map((item) => ({
          key: item.walletTransaction.id,
          no: item.id,
          transId: item.walletTransaction.walletTnxId || "N/A",
          type: formatValue(item.walletTransaction.userTransactionType),
          rate: item.walletTransaction.rate || "N/A",
          amount:
            formatCurrencyAmount(
              item.walletTransaction.toWallet.currency,
              item.walletTransaction.amount,
            ) || "N/A",
          narration: extractedString(item?.walletTransaction.narration),
          fee: formatCurrencyAmount(
            capitalizeCurrencyCode(currencyCode),
            item.walletTransaction.fee ?? 0,
          ),
          status: item.walletTransaction.tnxStatus || "N/a",
          date: dayjs(item.walletTransaction.createdAt || "N/A").format(
            "DD.MM.YYYY ⎮ HH:mm",
          ),
          transactionType: "Credit",
        }));

        setCreditData(filterData(payInData));

        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
      console.error(
        "An error occurred while fetching payout transactions:",
        error,
      );
    } finally {
      setLoading(false);
    }
  };

  const getDebitTransaction = async () => {
    setLoading(true);
    try {
      const commonData = {
        startDay: startDate,
        endDay: endDate,
        payoutStatus: select || status,
        pageNumber: pageNumber,
        pageSize: 10,
      };

      let payload;

      switch (capitalizeCurrencyCode(currencyCode)) {
        case "NGN":
          payload = {
            ...commonData,
            walletId: currentWallet?.id,
          };
          break;

        case "USD":
          payload = {
            ...commonData,
            fromWallet: currentWallet?.id,
          };
          break;

        case "GHS":
          payload = {
            ...commonData,
            fromWallet: currentWallet?.id,
          };
          break;

        case "USDT":
        case "USDC":
          payload = {
            ...commonData,
            fromWallet: currentWallet?.id,
            businessId,
          };
          break;

        case currentWallet.currency:
          payload = {
            ...commonData,
            toWallet: currentWallet?.id,
          };
          break;

        default:
          payload = commonData;
          break;
      }

      const data = await getPayoutTransactions(
        payload,
        capitalizeCurrencyCode(currencyCode),
        currentWallet?.id,
        businessId,
      );

      if (capitalizeCurrencyCode(currencyCode) === "NGN") {
        const tableData = data?.foreignBankTransferRequests?.map((item) => ({
          key: item.id,
          accountName: item.accountHolderName || "N/A",
          amount:
            formatCurrencyAmount(item?.sourceCurrency, item?.amount) || "N/A",
          accountNumber: item?.accountNumber || "N/A",
          status: item?.payoutStatus || "N/A",
          fee: formatCurrencyAmount(item?.sourceCurrency, item?.fee) || "N/A",
          date: dayjs(item.updatedAt).format("DD.MM.YYYY - HH:mm"),
          destinationCurrency: item?.destinationCurrency || "N/A",
          rate: item?.rate || "N/A",
          transactionType: "Debit",
          bankName: formatBankName(item.bankName),
          narration: capitalizeName(item?.narration) || "N/A",
          requestId: item.requestId,
        }));
        setDebitData(filterData(tableData));
      } else if (capitalizeCurrencyCode(currencyCode) === "ZAR") {
        const tableData = data.map((item) => ({
          key: item.id,
          accountName: item.accountName.length > 1 ? item.accountName : "N/A",
          accountNumber: item.accountNumber || "N/A",
          bankName: formatBankName(item.bankName),
          amount:
            formatCurrencyAmount(item.wallet.currency, item.amount) || "N/A",
          date: dayjs(item.createdAt).format("DD.MM.YYYY - HH:mm") || "N/A",
          fee: formatCurrencyAmount(currencyCode, item.fee),
          status: item.payoutStatus || "N/A",
          transactionType: "Debit",
          requestId: item.requestId,
          narration: capitalizeName(item?.narration) || "N/A",
        }));
        setDebitData(tableData);
      } else if (
        stableCoinsArray.includes(currencyCode?.toLowerCase()) ||
        ["USDC", "USDT"].includes(capitalizeCurrencyCode(currencyCode))
      ) {
        const tableData = data?.walletTransactions?.map((item) => {
          const tx = item.walletTransaction;
          const beneficiaryName = tx?.toWallet?.tag || "N/A";
          const accountNo = tx?.toWallet?.accountNo || "N/A";
          const currency =
            tx?.fromWallet?.currency || capitalizeCurrencyCode(currencyCode);

          return {
            key: tx?.id,
            transId: tx?.walletTnxId || "N/A",
            requestId: tx?.walletTnxId || "N/A",
            type: formatValue(tx?.userTransactionType),
            accountName: beneficiaryName,
            beneficiary: beneficiaryName,
            name: beneficiaryName,
            accountNumber: accountNo,
            acct: accountNo,
            bankName: "N/A",
            amount: formatCurrencyAmount(currency, tx?.amount) || "N/A",
            rate: tx?.rate || "N/A",
            date: dayjs(tx?.createdAt).format("DD.MM.YYYY - HH:mm"),
            fee: formatCurrencyAmount(
              capitalizeCurrencyCode(currencyCode),
              tx?.fee ?? 0,
            ),
            status: tx?.tnxStatus || "N/A",
            transactionType: "Debit",
            narration: tx?.narration
              ? capitalizeName(extractedString(tx.narration))
              : "N/A",
          };
        });
        setDebitData(filterData(tableData));
      } else {
        const payOutData = (Array.isArray(data) ? data : []).map((item) => ({
          key: item.id,
          beneficiary: item.accountName || "N/A",
          amount:
            formatCurrencyAmount(item.wallet.currency, item.amount) || "N/A",
          accountNumber: item.accountNumber || "N/A",
          accountName: item.accountName || "N/A",
          bankName: formatBankName(item.bankName),
          status: item.payoutStatus || "N/A",
          date: dayjs(item.createdAt).format("DD.MM.YYYY - HH:mm"),
          fee:
            formatCurrencyAmount(
              capitalizeCurrencyCode(currencyCode),
              item.fee,
            ) || 0,
          transactionType: "Debit",
          narration: capitalizeName(item?.narration) || "N/A",
          requestId: item.requestId,
        }));
        setDebitData(filterData(payOutData));
      }

      setTableData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(
        "An error occurred while fetching payout transactions:",
        error,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeToggle === "Credit") {
      getCreditTransaction().then((r) => r);
    }
    if (activeToggle === "Debit") {
      getDebitTransaction().then((r) => r);
    }
  }, [
    currencyCode,
    pageNumber,
    startDate,
    endDate,
    select,
    status,
    search,
    activeToggle,
  ]);
  const handleDrawer = (row) => {
    setState((prev) => ({
      ...prev,
      openDrawer: true,
      data: row,
    }));
  };

  const handleCloseExportByFilterModal = () => {
    setOpenExportFilterModal(!openExportFilterModal);
  };

  const noPermissionToExport = useMemo(() => !canUserExport("wallet", permissions), [permissions]);
  
  const handleGetWalletStatement = (data, clearForm) => {
    if (data.startDate > data.endDate) {
      onShowErrorAlert('', 'The Start Date cannot be later than the End Date');
      return;
    }

    if (noPermissionToExport) {
      onShowErrorAlert("", "Access is denied");
      return;
    }

    initiateWalletStatementExport(
      {
        walletId: currentWallet?.id,
        startDate: convertToDateOnly(data.startDate) ?? "",
        endDate: convertToDateOnly(data.endDate) ?? "",
        format: fileFormatApiMap[data.fileFormat],
      },
      {
        onSuccess: (result) => {
          onShowSuccessAlert(result?.message ?? 'Wallet statement sent to your email');
          clearForm();
          setOpenExportFilterModal(false);
        },
        onError: (error: unknown) => {
          onShowErrorAlert('', getErrorMessage(error, 'Unexpected error, please try again'));
        },
      },
    );
  };

  return (
    <StyledTransactionsTable width={width}>
      <Loader isLoading={loading || walletExportIsLoading} />
      <h3>{width > 768 ? TABLE_TITLE : ""}</h3>

      {
        <React.Fragment>
          <Tools
            search={search}
            setSearch={setSearch}
            onStatusChange={(data) => setStatus(data)}
            onDateChange={(date) => setDate(date)}
            onFilterButtonClick={filter}
            onResetButtonClick={handleReset}
            onSelectChange={(data) => setSelect(data)}
            handleExportClick={setOpenExportFilterModal}
          />

          <CustomModal
            isModalVisible={openExportFilterModal}
            onClose={handleCloseExportByFilterModal}
            width="464px"
            noPadding={true}
          >
            <ExportByFilterModal
              exportByFilterFunction={handleGetWalletStatement}
              modalTitle={`Get ${currentWallet?.currency} wallet statement`}
              modalSubTitle="Send your statement to your email"
              submitLabel="Send statement"
              noLimit
            />
          </CustomModal>

          <ToggleContainer>
            <ToggleButton
              toggleType={toggleType}
              activeToggle={activeToggle}
              setActiveToggle={setActiveToggle}
              handleToggle={handleToggle}
            />
          </ToggleContainer>
        </React.Fragment>
      }

      {activeToggle === "Debit" && (
        <>
          {width > 768 ? (
            <CustomTable
              extraEmptyChildren={<EmptyBalanceDescription />}
              columns={DEBIT_COLUMN}
              dataSource={debitData}
              currentPage={pageNumber}
              pageSize={10}
              totalItemCount={tableData?.totalCount || 0}
              onPageChange={(page) => {
                setPageNumber(page);
              }}
              handleRowClick={handleDrawer}
            />
          ) : (
            <MobileBody>
              <CustomMobileTable
                data={debitData}
                WalletCard={WalletCard}
                EmptyMobileCard={EmptyMobileCard}
                currentPage={tableData?.pageNumber}
                totalItemCount={tableData?.totalCount}
                onPageChange={onPageChange}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50]}
              />
            </MobileBody>
          )}
        </>
      )}

      {activeToggle === "Credit" && (
        <>
          {width > 768 ? (
            <CustomTable
              extraEmptyChildren={<EmptyBalanceDescription />}
              columns={payIntableColumns}
              dataSource={creditData}
              currentPage={pageNumber}
              totalItemCount={totalPayInData?.totalCount}
              onPageChange={onPageChange}
              handleRowClick={handleDrawer}
            />
          ) : (
            <MobileBody>
              <CustomMobileTable
                data={creditData}
                WalletCard={WalletCard}
                EmptyMobileCard={EmptyMobileCard}
                currentPage={pageNumber}
                totalItemCount={totalPayInData?.totalCount}
                onPageChange={onPageChange}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50]}
              />
            </MobileBody>
          )}
        </>
      )}

      <Drawer
        openDrawer={state.openDrawer}
        setState={setState}
        data={state.data}
      />
    </StyledTransactionsTable>
  );
};

export default Table;
